// Backend integration point: Replace all mock data with real API calls

export type Language = 'fr' | 'ru';

export type UserRank =
  | 'Étudiant' |'Apprenti' |'Assistant Comptable' |'Comptable' |'Comptable Senior' |'Expert Comptable';

export interface User {
  id: string;
  name: string;
  email: string;
  avatar: string;
  rank: UserRank;
  xp: number;
  weeklyXp: number;
  streak: number;
  lessonsCompleted: number;
  accuracyRate: number;
  language: Language;
  joinedAt: string;
  isAdmin: boolean;
}

export interface Achievement {
  id: string;
  key: string;
  title: { fr: string; ru: string };
  description: { fr: string; ru: string };
  icon: string;
  xpReward: number;
  unlockedAt?: string;
  isUnlocked: boolean;
}

export interface Lesson {
  id: string;
  levelNumber: number;
  lessonNumber: number;
  title: { fr: string; ru: string };
  description: { fr: string; ru: string };
  xpReward: number;
  status: 'locked' | 'available' | 'in_progress' | 'completed';
  completedAt?: string;
  accuracy?: number;
}

export interface Level {
  id: string;
  number: number;
  title: { fr: string; ru: string };
  description: { fr: string; ru: string };
  icon: string;
  color: string;
  lessons: Lesson[];
  completionPercent: number;
  isUnlocked: boolean;
}

export interface LeaderboardEntry {
  id: string;
  rank: number;
  name: string;
  avatar: string;
  xp: number;
  weeklyXp: number;
  streak: number;
  rankTitle: UserRank;
  isCurrentUser: boolean;
  country: string;
}

export interface AdminStat {
  label: { fr: string; ru: string };
  value: number | string;
  change: number;
  icon: string;
  color: string;
}

// ─── Current User Mock ───────────────────────────────────────────────────────
export const currentUser: User = {
  id: 'user-001',
  name: 'Sophie Moreau',
  email: 'sophie.moreau@vikacomptable.fr',
  avatar: 'SM',
  rank: 'Comptable',
  xp: 4250,
  weeklyXp: 680,
  streak: 12,
  lessonsCompleted: 23,
  accuracyRate: 87,
  language: 'fr',
  joinedAt: '2026-01-15',
  isAdmin: false,
};

// ─── XP Chart Data ────────────────────────────────────────────────────────────
export const xpChartData = [
  { day: 'Lun', xp: 120 },
  { day: 'Mar', xp: 85 },
  { day: 'Mer', xp: 210 },
  { day: 'Jeu', xp: 160 },
  { day: 'Ven', xp: 95 },
  { day: 'Sam', xp: 180 },
  { day: 'Dim', xp: 140 },
];

// ─── Achievements ─────────────────────────────────────────────────────────────
export const achievements: Achievement[] = [
  {
    id: 'ach-001',
    key: 'first_lesson',
    title: { fr: 'Première Leçon', ru: 'Первый урок' },
    description: { fr: 'Complétez votre première leçon', ru: 'Завершите первый урок' },
    icon: '🎓',
    xpReward: 50,
    unlockedAt: '2026-01-15',
    isUnlocked: true,
  },
  {
    id: 'ach-002',
    key: 'five_lessons',
    title: { fr: '5 Leçons Complétées', ru: '5 уроков завершено' },
    description: { fr: 'Complétez 5 leçons', ru: 'Завершите 5 уроков' },
    icon: '📚',
    xpReward: 100,
    unlockedAt: '2026-01-22',
    isUnlocked: true,
  },
  {
    id: 'ach-003',
    key: 'ten_lessons',
    title: { fr: '10 Leçons Complétées', ru: '10 уроков завершено' },
    description: { fr: 'Complétez 10 leçons', ru: 'Завершите 10 уроков' },
    icon: '🏆',
    xpReward: 200,
    unlockedAt: '2026-02-05',
    isUnlocked: true,
  },
  {
    id: 'ach-004',
    key: 'streak_7',
    title: { fr: 'Série de 7 Jours', ru: 'Серия 7 дней' },
    description: { fr: 'Maintenez une série de 7 jours', ru: 'Сохраняйте серию 7 дней' },
    icon: '🔥',
    xpReward: 150,
    unlockedAt: '2026-02-12',
    isUnlocked: true,
  },
  {
    id: 'ach-005',
    key: 'streak_30',
    title: { fr: 'Série de 30 Jours', ru: 'Серия 30 дней' },
    description: { fr: 'Maintenez une série de 30 jours', ru: 'Сохраняйте серию 30 дней' },
    icon: '⚡',
    xpReward: 500,
    isUnlocked: false,
  },
  {
    id: 'ach-006',
    key: 'hundred_correct',
    title: { fr: '100 Réponses Correctes', ru: '100 правильных ответов' },
    description: { fr: 'Donnez 100 réponses correctes', ru: 'Дайте 100 правильных ответов' },
    icon: '✅',
    xpReward: 300,
    isUnlocked: false,
  },
  {
    id: 'ach-007',
    key: 'vat_master',
    title: { fr: 'Maître de la TVA', ru: 'Мастер НДС' },
    description: { fr: 'Complétez le niveau TVA', ru: 'Завершите уровень НДС' },
    icon: '💼',
    xpReward: 250,
    isUnlocked: false,
  },
  {
    id: 'ach-008',
    key: 'accounting_champion',
    title: { fr: 'Champion Comptable', ru: 'Чемпион бухгалтерии' },
    description: { fr: 'Complétez tous les niveaux', ru: 'Завершите все уровни' },
    icon: '🌟',
    xpReward: 1000,
    isUnlocked: false,
  },
];

// ─── Levels & Lessons ─────────────────────────────────────────────────────────
export const levels: Level[] = [
  {
    id: 'level-01',
    number: 1,
    title: { fr: 'Introduction à la Comptabilité', ru: 'Введение в бухгалтерию' },
    description: { fr: 'Les bases absolues de la comptabilité', ru: 'Абсолютные основы бухгалтерии' },
    icon: '📖',
    color: '#10B981',
    completionPercent: 100,
    isUnlocked: true,
    lessons: [
      { id: 'l-01-01', levelNumber: 1, lessonNumber: 1, title: { fr: 'Qu\'est-ce que la comptabilité?', ru: 'Что такое бухгалтерия?' }, description: { fr: 'Définition et rôle', ru: 'Определение и роль' }, xpReward: 50, status: 'completed', completedAt: '2026-01-15', accuracy: 92 },
      { id: 'l-01-02', levelNumber: 1, lessonNumber: 2, title: { fr: 'Les acteurs de la comptabilité', ru: 'Участники бухгалтерии' }, description: { fr: 'Qui fait quoi?', ru: 'Кто что делает?' }, xpReward: 50, status: 'completed', completedAt: '2026-01-16', accuracy: 88 },
      { id: 'l-01-03', levelNumber: 1, lessonNumber: 3, title: { fr: 'Les documents comptables', ru: 'Бухгалтерские документы' }, description: { fr: 'Factures, reçus, journaux', ru: 'Счета, квитанции, журналы' }, xpReward: 60, status: 'completed', completedAt: '2026-01-17', accuracy: 95 },
    ],
  },
  {
    id: 'level-02',
    number: 2,
    title: { fr: 'Concepts Comptables', ru: 'Концепции бухгалтерии' },
    description: { fr: 'Les principes fondamentaux', ru: 'Основополагающие принципы' },
    icon: '💡',
    color: '#3B82F6',
    completionPercent: 100,
    isUnlocked: true,
    lessons: [
      { id: 'l-02-01', levelNumber: 2, lessonNumber: 1, title: { fr: 'Le principe de continuité', ru: 'Принцип непрерывности' }, description: { fr: 'L\'entreprise continue son activité', ru: 'Предприятие продолжает деятельность' }, xpReward: 70, status: 'completed', completedAt: '2026-01-20', accuracy: 84 },
      { id: 'l-02-02', levelNumber: 2, lessonNumber: 2, title: { fr: 'Le principe de prudence', ru: 'Принцип осторожности' }, description: { fr: 'Ne pas anticiper les profits', ru: 'Не предвосхищать прибыль' }, xpReward: 70, status: 'completed', completedAt: '2026-01-21', accuracy: 90 },
      { id: 'l-02-03', levelNumber: 2, lessonNumber: 3, title: { fr: 'L\'équation comptable', ru: 'Бухгалтерское уравнение' }, description: { fr: 'Actif = Passif + Capitaux', ru: 'Актив = Пассив + Капитал' }, xpReward: 80, status: 'completed', completedAt: '2026-01-22', accuracy: 78 },
    ],
  },
  {
    id: 'level-03',
    number: 3,
    title: { fr: 'Débit et Crédit', ru: 'Дебет и Кредит' },
    description: { fr: 'La règle fondamentale de la comptabilité', ru: 'Фундаментальное правило бухгалтерии' },
    icon: '⚖️',
    color: '#8B5CF6',
    completionPercent: 67,
    isUnlocked: true,
    lessons: [
      { id: 'l-03-01', levelNumber: 3, lessonNumber: 1, title: { fr: 'Comprendre le débit', ru: 'Понимание дебета' }, description: { fr: 'Qu\'est-ce que le débit?', ru: 'Что такое дебет?' }, xpReward: 80, status: 'completed', completedAt: '2026-02-01', accuracy: 91 },
      { id: 'l-03-02', levelNumber: 3, lessonNumber: 2, title: { fr: 'Comprendre le crédit', ru: 'Понимание кредита' }, description: { fr: 'Qu\'est-ce que le crédit?', ru: 'Что такое кредит?' }, xpReward: 80, status: 'completed', completedAt: '2026-02-02', accuracy: 86 },
      { id: 'l-03-03', levelNumber: 3, lessonNumber: 3, title: { fr: 'La règle du débit-crédit', ru: 'Правило дебет-кредит' }, description: { fr: 'Exercices pratiques', ru: 'Практические упражнения' }, xpReward: 100, status: 'in_progress' },
    ],
  },
  {
    id: 'level-04',
    number: 4,
    title: { fr: 'Écritures Comptables', ru: 'Бухгалтерские записи' },
    description: { fr: 'Enregistrer les transactions', ru: 'Запись транзакций' },
    icon: '📝',
    color: '#F59E0B',
    completionPercent: 0,
    isUnlocked: true,
    lessons: [
      { id: 'l-04-01', levelNumber: 4, lessonNumber: 1, title: { fr: 'Introduction aux écritures', ru: 'Введение в записи' }, description: { fr: 'Structure d\'une écriture', ru: 'Структура записи' }, xpReward: 90, status: 'available' },
      { id: 'l-04-02', levelNumber: 4, lessonNumber: 2, title: { fr: 'Écritures de vente', ru: 'Записи продаж' }, description: { fr: 'Enregistrer une vente', ru: 'Запись продажи' }, xpReward: 100, status: 'locked' },
      { id: 'l-04-03', levelNumber: 4, lessonNumber: 3, title: { fr: 'Écritures d\'achat', ru: 'Записи покупок' }, description: { fr: 'Enregistrer un achat', ru: 'Запись покупки' }, xpReward: 100, status: 'locked' },
    ],
  },
  {
    id: 'level-05',
    number: 5,
    title: { fr: 'Factures', ru: 'Счета-фактуры' },
    description: { fr: 'Analyser et enregistrer les factures', ru: 'Анализ и запись счетов' },
    icon: '🧾',
    color: '#EC4899',
    completionPercent: 0,
    isUnlocked: false,
    lessons: [
      { id: 'l-05-01', levelNumber: 5, lessonNumber: 1, title: { fr: 'Structure d\'une facture', ru: 'Структура счета' }, description: { fr: 'Les éléments obligatoires', ru: 'Обязательные элементы' }, xpReward: 100, status: 'locked' },
      { id: 'l-05-02', levelNumber: 5, lessonNumber: 2, title: { fr: 'Facture d\'achat', ru: 'Счет покупки' }, description: { fr: 'Analyser une facture fournisseur', ru: 'Анализ счета поставщика' }, xpReward: 110, status: 'locked' },
      { id: 'l-05-03', levelNumber: 5, lessonNumber: 3, title: { fr: 'Facture de vente', ru: 'Счет продажи' }, description: { fr: 'Créer une facture client', ru: 'Создание счета клиента' }, xpReward: 110, status: 'locked' },
    ],
  },
  {
    id: 'level-06',
    number: 6,
    title: { fr: 'TVA', ru: 'НДС' },
    description: { fr: 'Taxe sur la valeur ajoutée', ru: 'Налог на добавленную стоимость' },
    icon: '💰',
    color: '#14B8A6',
    completionPercent: 0,
    isUnlocked: false,
    lessons: [
      { id: 'l-06-01', levelNumber: 6, lessonNumber: 1, title: { fr: 'Qu\'est-ce que la TVA?', ru: 'Что такое НДС?' }, description: { fr: 'Principe et taux', ru: 'Принцип и ставки' }, xpReward: 120, status: 'locked' },
      { id: 'l-06-02', levelNumber: 6, lessonNumber: 2, title: { fr: 'Calcul de TVA', ru: 'Расчет НДС' }, description: { fr: 'TVA collectée vs déductible', ru: 'Собранный vs вычитаемый НДС' }, xpReward: 130, status: 'locked' },
      { id: 'l-06-03', levelNumber: 6, lessonNumber: 3, title: { fr: 'Déclaration de TVA', ru: 'Декларация НДС' }, description: { fr: 'Remplir la déclaration', ru: 'Заполнение декларации' }, xpReward: 150, status: 'locked' },
    ],
  },
  {
    id: 'level-07',
    number: 7,
    title: { fr: 'Grand Livre', ru: 'Главная книга' },
    description: { fr: 'Organiser les comptes', ru: 'Организация счетов' },
    icon: '📊',
    color: '#6366F1',
    completionPercent: 0,
    isUnlocked: false,
    lessons: [
      { id: 'l-07-01', levelNumber: 7, lessonNumber: 1, title: { fr: 'Structure du grand livre', ru: 'Структура главной книги' }, description: { fr: 'Organisation des comptes', ru: 'Организация счетов' }, xpReward: 140, status: 'locked' },
      { id: 'l-07-02', levelNumber: 7, lessonNumber: 2, title: { fr: 'Balance de vérification', ru: 'Проверочный баланс' }, description: { fr: 'Contrôler les soldes', ru: 'Контроль остатков' }, xpReward: 150, status: 'locked' },
    ],
  },
  {
    id: 'level-08',
    number: 8,
    title: { fr: 'Bilan', ru: 'Баланс' },
    description: { fr: 'La situation financière', ru: 'Финансовое положение' },
    icon: '📋',
    color: '#F97316',
    completionPercent: 0,
    isUnlocked: false,
    lessons: [
      { id: 'l-08-01', levelNumber: 8, lessonNumber: 1, title: { fr: 'Structure du bilan', ru: 'Структура баланса' }, description: { fr: 'Actif et passif', ru: 'Актив и пассив' }, xpReward: 160, status: 'locked' },
      { id: 'l-08-02', levelNumber: 8, lessonNumber: 2, title: { fr: 'Lire un bilan', ru: 'Чтение баланса' }, description: { fr: 'Interpréter les données', ru: 'Интерпретация данных' }, xpReward: 170, status: 'locked' },
    ],
  },
  {
    id: 'level-09',
    number: 9,
    title: { fr: 'Compte de Résultat', ru: 'Отчет о прибылях' },
    description: { fr: 'Les performances de l\'entreprise', ru: 'Результаты деятельности' },
    icon: '📈',
    color: '#84CC16',
    completionPercent: 0,
    isUnlocked: false,
    lessons: [
      { id: 'l-09-01', levelNumber: 9, lessonNumber: 1, title: { fr: 'Structure du compte de résultat', ru: 'Структура отчета' }, description: { fr: 'Produits et charges', ru: 'Доходы и расходы' }, xpReward: 180, status: 'locked' },
      { id: 'l-09-02', levelNumber: 9, lessonNumber: 2, title: { fr: 'Calculer le résultat net', ru: 'Расчет чистой прибыли' }, description: { fr: 'Profit ou perte?', ru: 'Прибыль или убыток?' }, xpReward: 190, status: 'locked' },
    ],
  },
  {
    id: 'level-10',
    number: 10,
    title: { fr: 'Comptabilité Professionnelle', ru: 'Профессиональная бухгалтерия' },
    description: { fr: 'Niveau expert — tout intégrer', ru: 'Экспертный уровень — интеграция всего' },
    icon: '🏆',
    color: '#EF4444',
    completionPercent: 0,
    isUnlocked: false,
    lessons: [
      { id: 'l-10-01', levelNumber: 10, lessonNumber: 1, title: { fr: 'Clôture des comptes', ru: 'Закрытие счетов' }, description: { fr: 'Fin d\'exercice comptable', ru: 'Конец отчетного периода' }, xpReward: 200, status: 'locked' },
      { id: 'l-10-02', levelNumber: 10, lessonNumber: 2, title: { fr: 'Audit et contrôle', ru: 'Аудит и контроль' }, description: { fr: 'Vérification des comptes', ru: 'Проверка счетов' }, xpReward: 220, status: 'locked' },
      { id: 'l-10-03', levelNumber: 10, lessonNumber: 3, title: { fr: 'Certification professionnelle', ru: 'Профессиональная сертификация' }, description: { fr: 'Examen final', ru: 'Финальный экзамен' }, xpReward: 500, status: 'locked' },
    ],
  },
];

// ─── Leaderboard ──────────────────────────────────────────────────────────────
export const leaderboardData: LeaderboardEntry[] = [
  { id: 'lb-001', rank: 1, name: 'Alexandre Dupont', avatar: 'AD', xp: 12450, weeklyXp: 1840, streak: 45, rankTitle: 'Expert Comptable', isCurrentUser: false, country: 'FR' },
  { id: 'lb-002', rank: 2, name: 'Natalia Ivanova', avatar: 'NI', xp: 11200, weeklyXp: 1620, streak: 38, rankTitle: 'Expert Comptable', isCurrentUser: false, country: 'RU' },
  { id: 'lb-003', rank: 3, name: 'Pierre Lefebvre', avatar: 'PL', xp: 9800, weeklyXp: 1480, streak: 29, rankTitle: 'Comptable Senior', isCurrentUser: false, country: 'FR' },
  { id: 'lb-004', rank: 4, name: 'Elena Sorokina', avatar: 'ES', xp: 8650, weeklyXp: 1200, streak: 22, rankTitle: 'Comptable Senior', isCurrentUser: false, country: 'RU' },
  { id: 'lb-005', rank: 5, name: 'Marie Fontaine', avatar: 'MF', xp: 7300, weeklyXp: 980, streak: 18, rankTitle: 'Comptable', isCurrentUser: false, country: 'FR' },
  { id: 'lb-006', rank: 6, name: 'Dmitri Volkov', avatar: 'DV', xp: 6100, weeklyXp: 860, streak: 14, rankTitle: 'Comptable', isCurrentUser: false, country: 'RU' },
  { id: 'lb-007', rank: 7, name: 'Camille Bernard', avatar: 'CB', xp: 5200, weeklyXp: 740, streak: 11, rankTitle: 'Assistant Comptable', isCurrentUser: false, country: 'FR' },
  { id: 'lb-008', rank: 8, name: 'Sophie Moreau', avatar: 'SM', xp: 4250, weeklyXp: 680, streak: 12, rankTitle: 'Comptable', isCurrentUser: true, country: 'FR' },
  { id: 'lb-009', rank: 9, name: 'Andrei Petrov', avatar: 'AP', xp: 3800, weeklyXp: 520, streak: 8, rankTitle: 'Assistant Comptable', isCurrentUser: false, country: 'RU' },
  { id: 'lb-010', rank: 10, name: 'Isabelle Martin', avatar: 'IM', xp: 3200, weeklyXp: 460, streak: 6, rankTitle: 'Apprenti', isCurrentUser: false, country: 'FR' },
  { id: 'lb-011', rank: 11, name: 'Sergei Kozlov', avatar: 'SK', xp: 2900, weeklyXp: 380, streak: 5, rankTitle: 'Apprenti', isCurrentUser: false, country: 'RU' },
  { id: 'lb-012', rank: 12, name: 'Lucie Rousseau', avatar: 'LR', xp: 2400, weeklyXp: 290, streak: 3, rankTitle: 'Étudiant', isCurrentUser: false, country: 'FR' },
];

// ─── Admin Users ──────────────────────────────────────────────────────────────
export const adminUsers = [
  { id: 'user-001', name: 'Sophie Moreau', email: 'sophie.moreau@vikacomptable.fr', rank: 'Comptable', xp: 4250, streak: 12, lessonsCompleted: 23, status: 'active', joinedAt: '2026-01-15', country: 'FR' },
  { id: 'user-002', name: 'Alexandre Dupont', email: 'alexandre.dupont@gmail.com', rank: 'Expert Comptable', xp: 12450, streak: 45, lessonsCompleted: 87, status: 'active', joinedAt: '2025-11-02', country: 'FR' },
  { id: 'user-003', name: 'Natalia Ivanova', email: 'natalia.ivanova@mail.ru', rank: 'Expert Comptable', xp: 11200, streak: 38, lessonsCompleted: 79, status: 'active', joinedAt: '2025-12-10', country: 'RU' },
  { id: 'user-004', name: 'Pierre Lefebvre', email: 'pierre.lefebvre@yahoo.fr', rank: 'Comptable Senior', xp: 9800, streak: 29, lessonsCompleted: 68, status: 'active', joinedAt: '2025-10-18', country: 'FR' },
  { id: 'user-005', name: 'Elena Sorokina', email: 'elena.sorokina@yandex.ru', rank: 'Comptable Senior', xp: 8650, streak: 22, lessonsCompleted: 61, status: 'suspended', joinedAt: '2026-01-05', country: 'RU' },
  { id: 'user-006', name: 'Marie Fontaine', email: 'marie.fontaine@orange.fr', rank: 'Comptable', xp: 7300, streak: 18, lessonsCompleted: 52, status: 'active', joinedAt: '2025-09-22', country: 'FR' },
  { id: 'user-007', name: 'Dmitri Volkov', email: 'dmitri.volkov@mail.ru', rank: 'Comptable', xp: 6100, streak: 14, lessonsCompleted: 44, status: 'active', joinedAt: '2026-02-01', country: 'RU' },
  { id: 'user-008', name: 'Camille Bernard', email: 'camille.bernard@sfr.fr', rank: 'Assistant Comptable', xp: 5200, streak: 11, lessonsCompleted: 37, status: 'active', joinedAt: '2026-02-14', country: 'FR' },
];

// ─── Admin Stats ──────────────────────────────────────────────────────────────
export const adminStats: AdminStat[] = [
  { label: { fr: 'Utilisateurs Totaux', ru: 'Всего пользователей' }, value: 1284, change: 8.2, icon: 'Users', color: '#7C3AED' },
  { label: { fr: 'Leçons Complétées Aujourd\'hui', ru: 'Уроков завершено сегодня' }, value: 342, change: 12.5, icon: 'BookOpen', color: '#10B981' },
  { label: { fr: 'Séries Actives', ru: 'Активные серии' }, value: 867, change: -3.1, icon: 'Flame', color: '#F59E0B' },
  { label: { fr: 'Taux de Rétention', ru: 'Коэффициент удержания' }, value: '78%', change: 2.4, icon: 'TrendingUp', color: '#3B82F6' },
];

// ─── Lesson Content ───────────────────────────────────────────────────────────
export const lessonContent = {
  'l-03-03': {
    id: 'l-03-03',
    title: { fr: 'La règle du débit-crédit', ru: 'Правило дебет-кредит' },
    steps: [
      {
        type: 'theory' as const,
        title: { fr: 'La règle fondamentale', ru: 'Фундаментальное правило' },
        content: {
          fr: `La comptabilité en partie double est basée sur une règle simple: **chaque transaction affecte au moins deux comptes**.

La règle est: **Débit = Crédit**

Pour chaque écriture comptable:
- Le total des débits doit toujours égaler le total des crédits
- Un débit augmente les actifs et les charges
- Un crédit augmente les passifs, les capitaux propres et les produits`,
          ru: `Двойная бухгалтерия основана на простом правиле: **каждая транзакция затрагивает как минимум два счета**.

Правило: **Дебет = Кредит**

Для каждой бухгалтерской записи:
- Сумма дебетов всегда должна равняться сумме кредитов
- Дебет увеличивает активы и расходы
- Кредит увеличивает пассивы, собственный капитал и доходы`,
        },
      },
      {
        type: 'example' as const,
        title: { fr: 'Exemple pratique', ru: 'Практический пример' },
        content: {
          fr: `**Exemple:** L'entreprise achète du matériel pour 1 000 €, payé en espèces.

| Compte | Débit | Crédit |
|--------|-------|--------|
| Matériel (Actif ↑) | 1 000 € | |
| Caisse (Actif ↓) | | 1 000 € |

**Analyse:** Le matériel augmente (débit), la caisse diminue (crédit). L'équation reste équilibrée.`,
          ru: `**Пример:** Компания покупает оборудование за 1 000 €, оплата наличными.

| Счет | Дебет | Кредит |
|------|-------|--------|
| Оборудование (Актив ↑) | 1 000 € | |
| Касса (Актив ↓) | | 1 000 € |

**Анализ:** Оборудование увеличивается (дебет), касса уменьшается (кредит). Уравнение остается сбалансированным.`,
        },
      },
      {
        type: 'exercise' as const,
        title: { fr: 'Exercice: Classifiez les comptes', ru: 'Упражнение: Классифицируйте счета' },
        question: {
          fr: 'L\'entreprise reçoit un paiement client de 500 €. Classifiez les mouvements:',
          ru: 'Компания получает платеж от клиента на 500 €. Классифицируйте движения:',
        },
        items: [
          { id: 'ex-01', account: { fr: 'Caisse', ru: 'Касса' }, correctAnswer: 'debit' },
          { id: 'ex-02', account: { fr: 'Créances clients', ru: 'Дебиторская задолженность' }, correctAnswer: 'credit' },
        ],
      },
      {
        type: 'quiz' as const,
        title: { fr: 'Quiz de validation', ru: 'Контрольный тест' },
        questions: [
          {
            id: 'q-01',
            question: {
              fr: 'Que se passe-t-il lorsqu\'on débite un compte d\'actif?',
              ru: 'Что происходит при дебетовании счета актива?',
            },
            options: [
              { id: 'q-01-a', text: { fr: 'Il diminue', ru: 'Он уменьшается' }, isCorrect: false },
              { id: 'q-01-b', text: { fr: 'Il augmente', ru: 'Он увеличивается' }, isCorrect: true },
              { id: 'q-01-c', text: { fr: 'Il reste inchangé', ru: 'Он остается неизменным' }, isCorrect: false },
              { id: 'q-01-d', text: { fr: 'Il est supprimé', ru: 'Он удаляется' }, isCorrect: false },
            ],
          },
          {
            id: 'q-02',
            question: {
              fr: 'Dans une écriture comptable, le total des débits doit:',
              ru: 'В бухгалтерской записи сумма дебетов должна:',
            },
            options: [
              { id: 'q-02-a', text: { fr: 'Être supérieur aux crédits', ru: 'Превышать кредиты' }, isCorrect: false },
              { id: 'q-02-b', text: { fr: 'Être inférieur aux crédits', ru: 'Быть меньше кредитов' }, isCorrect: false },
              { id: 'q-02-c', text: { fr: 'Être égal aux crédits', ru: 'Быть равна кредитам' }, isCorrect: true },
              { id: 'q-02-d', text: { fr: 'N\'avoir aucun lien avec les crédits', ru: 'Не иметь связи с кредитами' }, isCorrect: false },
            ],
          },
          {
            id: 'q-03',
            question: {
              fr: 'Un crédit sur un compte de passif:',
              ru: 'Кредит на счете пассива:',
            },
            options: [
              { id: 'q-03-a', text: { fr: 'Diminue le passif', ru: 'Уменьшает пассив' }, isCorrect: false },
              { id: 'q-03-b', text: { fr: 'Augmente le passif', ru: 'Увеличивает пассив' }, isCorrect: true },
              { id: 'q-03-c', text: { fr: 'N\'affecte pas le passif', ru: 'Не влияет на пассив' }, isCorrect: false },
              { id: 'q-03-d', text: { fr: 'Annule le passif', ru: 'Аннулирует пассив' }, isCorrect: false },
            ],
          },
        ],
      },
    ],
  },
};